import * as React from "react";
import {useCallback} from "react";
import {
    classNames,
    layout,
    LockStatusBadge,
    MaterialIcon,
    PushButton,
    theme,
    typography,
    useBoxLockStatus,
    useOccupancies,
    usePlatform,
    View,
    ViewContent,
    ViewFooter,
    ViewFooterItem,
    ViewHeader,
    ViewHeaderItem
} from "@variocube/cube-ui";
import {Box, LogLevel} from "@variocube/app-api";
import {Item, Rental, User} from "../types";
import {useCurrentRental, useItem} from "../hooks";
import {RentalDetail} from "./RentalDetail";
import {ItemDetail} from "./ItemDetail";
import {stylesheet} from "typestyle";
import {ItemRemoval} from "./types";
import {useLocalization} from "../../i18n";

export interface BoxViewProps {
    user?: User;
    box: Box;
    items: Item[];
    rentals: Rental[];
    onBack: () => void;
    onStoreBox: (box: Box) => void;
    onRemoveItem: (removal: ItemRemoval) => void;
}

export function BoxView({user, box, items, rentals, onBack, onStoreBox, onRemoveItem}: BoxViewProps) {
    const {t} = useLocalization();
    const occupancies = useOccupancies();
    const occupancy = occupancies.find(occupancy => occupancy.boxNumber == box.number);
    const item = useItem(occupancy?.content.item);
    const rental = useCurrentRental(occupancy?.content.item);

    const lockStatus = useBoxLockStatus(box.number);

    const platform = usePlatform();
    const handleOpen = useCallback(async () => {
        await platform.openBoxLock(box.number);
        await platform.log(LogLevel.Trace, "Open box command", {...user, box: box.number});
    }, [platform, box, user]);

    const handleRemove = useCallback(() => occupancy && item && onRemoveItem({occupancy, item, box}), [occupancy, item, box, onRemoveItem]);
    const handleStore = useCallback(() => onStoreBox(box), [box, onStoreBox]);

    return (
        <View>
            <ViewHeader>
                <ViewHeaderItem fill className={layout.paddingMedium}>
                    <div className={typography.title}>{t("Box")} {box.number}</div>
                    <div className={typography.description}>{box.types.join(", ")}</div>
                </ViewHeaderItem>
                <ViewHeaderItem className={layout.paddingMedium}>
                    {lockStatus && (
                        <LockStatusBadge lockStatus={lockStatus} strings={t}/>
                    )}
                </ViewHeaderItem>
                <ViewHeaderItem className={layout.paddingMedium}>
                    <PushButton onClick={handleOpen}>
                        {t("OpenBox")}
                    </PushButton>
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent className={layout.horizontal}>
                <ItemDetail
                    className={classNames(layout.fill, theme.moreContrast, layout.paddingMedium, styles.halfWidth)}
                    item={item}
                />
                <RentalDetail
                    className={classNames(layout.fill, theme.lessContrast, layout.paddingMedium, styles.halfWidth)}
                    rental={rental}
                />
            </ViewContent>
            <ViewFooter className={classNames(layout.paddingMedium, layout.spaceBetween)}>
                <ViewFooterItem>
                    <PushButton onClick={onBack} icon={MaterialIcon.arrow_back}>
                        {t("Back")}
                    </PushButton>
                </ViewFooterItem>
                <ViewFooterItem>
                    {!item && (
                        <PushButton onClick={handleStore} primary>
                            {t("StoreItem")}
                        </PushButton>
                    )}
                    {item && (
                        <PushButton onClick={handleRemove} primary>
                            {t("RemoveItem")}
                        </PushButton>
                    )}
                </ViewFooterItem>
            </ViewFooter>
        </View>
    )
}

const styles = stylesheet({
    halfWidth: {
        width: "50%"
    }
})